import { useState, useEffect, useMemo, useCallback, createContext, useContext } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CurrencyContext = createContext({
  symbol: '$',
  code: 'USD',
  position: 'before',
  decimals: 2,
  format: (amount) => `$${Number(amount || 0).toFixed(2)}`,
});

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState({ symbol: '$', code: 'USD', position: 'before', decimals: 2 });

  useEffect(() => {
    axios.get(`${API}/api/settings/global`)
      .then(res => {
        const c = res.data?.currency;
        if (c) {
          setCurrency({
            symbol: c.symbol || '$',
            code: c.code || 'USD',
            position: c.position || 'before',
            decimals: c.decimal_places ?? 2,
          });
        }
      })
      .catch(() => {});
  }, []);

  // Stable identity across renders unless `currency` actually changes.
  const format = useCallback((amount) => {
    const num = Number(amount || 0).toFixed(currency.decimals);
    const formatted = Number(num).toLocaleString(undefined, {
      minimumFractionDigits: currency.decimals,
      maximumFractionDigits: currency.decimals,
    });
    return currency.position === 'before'
      ? `${currency.symbol}${formatted}`
      : `${formatted}${currency.symbol}`;
  }, [currency]);

  const value = useMemo(() => ({ ...currency, format }), [currency, format]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
